import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Heading,
  Button,
  Spinner,
  Flex,
  Stack,
  Divider,
  Text,
  VStack,
  useToast,
  StackDivider,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import { useSubscription } from "../../context/SubscriptionContext";
import { useAuth } from "../../hooks/useAuth";
import axiosInstance from "../../services/axios";

const BillingInfo = () => {
  const { user, isAuthenticated } = useAuth();
  const toast = useToast();
  const {
    fetchPayoutHistory,
    createConnectAccount,
    goToOnboarding,
    getConnectAccountDetails,
    createExpressPayout,
  } = useSubscription();

  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [error, setError] = useState("");

  // store entire response
  const [eligibleData, setEligibleData] = useState({
    eligible_transactions: { eligible_amount: 0, total_amount: 0 },
    eligible_referral_balance: { eligible_amount: 0, total_amount: 0 },
    total_eligible_balance: 0,
    total_balance: 0,
  });

  const fetchAccount = useCallback(async () => {
    if (!isAuthenticated || user?.account_type !== "artist") return;
    
    setLoading(true);
    setError("");
    try {
      const data = await getConnectAccountDetails();
      setAccount(data);
    } catch (err) {
      setAccount(null);
      // Only show error for non-404 cases (404 means no account setup yet)
      if (err.message && !err.message.includes("404") && !err.message.includes("Not Found")) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [getConnectAccountDetails, isAuthenticated, user?.account_type]);

  const fetchPayouts = useCallback(async () => {
    if (!isAuthenticated || user?.account_type !== "artist") return;
    
    try {
      const response = await fetchPayoutHistory();
      setPayoutHistory(response?.payouts || []);
    } catch (err) {
      // Only show toast for unexpected errors (not 400/404)
      if (err.response?.status !== 400 && err.response?.status !== 404) {
        toast({
          title: "Failed to fetch payout history",
          description: err.response?.data?.detail || err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setPayoutHistory([]);
    }
  }, [toast, fetchPayoutHistory, isAuthenticated, user?.account_type]);

  const fetchBalances = useCallback(async () => {
    if (!isAuthenticated || user?.account_type !== "artist") return;
    
    try {
      const { data } = await axiosInstance.get(
"/users/eligible_balance"
      );
      setEligibleData({
        eligible_transactions: data.eligible_transactions,
        eligible_referral_balance: data.eligible_referral_balance,
        total_eligible_balance: data.total_eligible_balance,
        total_balance: data.total_balance,
      });
    } catch (err) {
      toast({
        title: "Failed to fetch balances",
        description: err.response?.data?.detail || err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast, isAuthenticated, user?.account_type]);

  useEffect(() => {
    // Only fetch billing data for authenticated artists
    if (isAuthenticated && user?.account_type === "artist") {
      fetchBalances(); // This endpoint works
      fetchAccount(); // Fetch Stripe Connect account details
      fetchPayouts(); // Fetch payout history
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.account_type]); // Run when auth status or user type changes

  // Compute the two values you want to show:
  const combinedTotal = eligibleData.total_balance;
  const combinedEligible = eligibleData.total_eligible_balance;

  const handleCreateAccount = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await createConnectAccount();
      toast({
        title: "Stripe Connect Account Created",
        description: response.message || "Account created successfully",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await fetchAccount();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboarding = async () => {
    setLoading(true);
    setError("");
    try {
      await goToOnboarding();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCashOut = async () => {
    setLoading(true);
    setError("");
    try {
      if (!account || !account.payouts_enabled) {
        toast({
          title: "Banking Info Required",
          description: "Please add your banking details before cashing out.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        
        return;
      }
      await createExpressPayout();
      toast({
        title: "Cash Out Initiated",
        description: "Your payout is being processed.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      // Refresh both balances and payout history after cash out
      await fetchBalances();
      await fetchPayouts();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };







  if (loading) {
    return (
      <Box maxW="800px" mx="auto" borderRadius="lg" bg="secondary.500" boxShadow="lg">
        {/* Balances & Earnings Skeleton */}
        <Skeleton height="28px" width="200px" mb={6} startColor="dark.500" endColor="secondary.700" />
        <VStack spacing={4} p={4} borderWidth="1px" borderRadius="lg" bg="secondary.500">
          <Flex justify="space-between" w="full">
            <Skeleton height="24px" width="150px" startColor="dark.500" endColor="secondary.700" />
            <Skeleton height="24px" width="80px" startColor="dark.500" endColor="secondary.700" />
          </Flex>
          <Flex justify="space-between" w="full">
            <Skeleton height="24px" width="120px" startColor="dark.500" endColor="secondary.700" />
            <Skeleton height="24px" width="80px" startColor="dark.500" endColor="secondary.700" />
          </Flex>
        </VStack>

        <Skeleton height="1px" my={6} startColor="dark.500" endColor="secondary.700" />

        {/* Stripe Account Section Skeleton */}
        <Skeleton height="28px" width="150px" mb={4} startColor="dark.500" endColor="secondary.700" />
        
        <Box p={6} borderRadius="lg">
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap">
            <Skeleton height="24px" width="140px" startColor="dark.500" endColor="secondary.700" />
            <Skeleton height="20px" width="200px" startColor="dark.500" endColor="secondary.700" />
          </Flex>
          <Skeleton height="1px" mb={4} startColor="dark.500" endColor="secondary.700" />
          
          <VStack spacing={3} mt={4}>
            <Skeleton height="20px" width="300px" startColor="dark.500" endColor="secondary.700" />
            <Skeleton height="48px" width="100%" borderRadius="md" startColor="dark.500" endColor="secondary.700" />
          </VStack>

          {/* Bank Accounts Skeleton */}
          <Box my={4}>
            <Skeleton height="20px" width="120px" mb={2} startColor="dark.500" endColor="secondary.700" />
            <VStack spacing={3} align="stretch">
              {Array(2).fill(0).map((_, index) => (
                <Box
                  key={index}
                  p={{ base: 3, md: 4 }}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="white"
                >
                  <VStack spacing={1} align="start">
                    <Skeleton height="16px" width="180px" startColor="gray.200" endColor="gray.300" />
                    <Skeleton height="14px" width="100px" startColor="gray.200" endColor="gray.300" />
                    <Skeleton height="14px" width="120px" startColor="gray.200" endColor="gray.300" />
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Box>
        </Box>

        {/* Payout History Skeleton */}
        <Box mb={6}>
          <Skeleton height="28px" width="150px" mb={4} startColor="dark.500" endColor="secondary.700" />
          <VStack spacing={4} align="stretch">
            {Array(3).fill(0).map((_, index) => (
              <Box
                key={index}
                p={{ base: 3, md: 4 }}
                borderWidth="1px"
                borderRadius="md"
                bg="secondary.100"
                shadow="sm"
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  gap={2}
                >
                  <Skeleton height="16px" width="120px" startColor="primary.700" endColor="primary.500" />
                  <Skeleton height="16px" width="100px" startColor="primary.700" endColor="primary.500" />
                  <Skeleton height="16px" width="80px" startColor="primary.700" endColor="primary.500" />
                </Flex>
              </Box>
            ))}
          </VStack>
        </Box>
        
        <Skeleton height="1px" my={6} startColor="dark.500" endColor="secondary.700" />
      </Box>
    );
  }

  return (
    <Box maxW="800px" mx="auto" p={4} borderRadius="lg" bg="secondary.500" boxShadow="lg">
      {/* Balances & Earnings Section */}
      <Heading color="dark.900" mb={6} textAlign="left" fontSize="xl">
        Balances & Earnings
      </Heading>
      <VStack spacing={4} p={4} borderWidth="1px" borderRadius="lg" bg="secondary.500">
        <Flex justify="space-between" w="full">
          <Text fontSize="lg" color="primary.500">Available Balance:</Text>
          <Text fontSize="lg" fontWeight="bold" color="primary.900">
            ${combinedEligible.toFixed(2)}
          </Text>
        </Flex>
        <Flex justify="space-between" w="full">
          <Text fontSize="lg" color="primary.500">Total Balance:</Text>
          <Text fontSize="lg" fontWeight="bold" color="primary.900">
            ${combinedTotal.toFixed(2)}
          </Text>
        </Flex>
      </VStack>

      <Divider borderColor="primary.500" my={6} />


      {/* Stripe Account Section */}
      <Heading fontSize="xl" color="dark.900" mb={4}>
        Stripe Account 
      </Heading>
      {error && (
        <Box mb={4} p={3} bg="red.100" borderRadius="md">
          <Text color="red.800">{error}</Text>
        </Box>
      )}
      {!account ? (
        <Stack spacing={4} align="center" bg="secondary.100" p={6} my={2} borderRadius="lg">
          <Text color="primary.100" fontSize="lg" textAlign="center">
            No Stripe Connect account found. Create one to enable payouts.
          </Text>
          <Button onClick={handleCreateAccount} size="lg" bg="green.500" color="white" _hover={{ bg: "green.600" }}>
            Create Stripe Connect Account
          </Button>
        </Stack>
      ) : (
        <Box p={6} borderRadius="lg" color="primary.100">
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap">
            <Text fontSize="lg" fontWeight="bold">
              account number:
            </Text>
            <Text fontSize="md" color="dark.900">
              {account.id}
            </Text>
          </Flex>
          <Divider borderColor="dark.900" mb={4} />

          {account.charges_enabled && account.payouts_enabled ? (
            <Stack spacing={3} mt={4}>
              <Text fontSize="md" color="primary.500" textAlign="center">
                Your account is fully enabled! You can now receive payouts.
              </Text>
              <Button
                onClick={handleCashOut}
                bg="green.500"
                color="dark.100"
                _hover={{ color: "dark.900", bg: "dark.100" }}
                size="lg"
                w="full"
              >
                Cash Out
              </Button>

            </Stack>
          ) : (
            <Button onClick={handleOnboarding} colorScheme="blue" size="lg" w="full" mt={4}>
              Complete Onboarding
            </Button>
          )}

          {account.external_accounts && account.external_accounts.length > 0 && (
            <Box my={4}>
              <Heading size="sm" mb={2} color="primary.500" textAlign={{ base: "center", md: "left" }}>
                Bank Accounts
              </Heading>
              <VStack
                spacing={3}
                divider={<StackDivider borderColor="gray.200" />}
                align="stretch"
              >
                {account.external_accounts.map((bank) => (
                  <Flex
                    key={bank.id}
                    p={{ base: 3, md: 4 }}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="white"
                    flexDirection={{ base: "column", md: "row" }}
                    justify="space-between"
                    align="center"
                  >
                    <VStack spacing={1} align="start">
                      <Text fontSize={{ base: "sm", md: "md" }} color="primary.900" fontWeight="semibold">
                        {bank.bank_name || "Unknown Bank"} (**** {bank.last4})
                      </Text>
                      <Text fontSize={{ base: "xs", md: "sm" }} color="primary.500">
                        {bank.country} • {bank.currency.toUpperCase()}
                      </Text>
                      <Text fontSize={{ base: "xs", md: "sm" }} color="primary.500">
                        Status: {bank.status.charAt(0).toUpperCase() + bank.status.slice(1)}
                      </Text>
                    </VStack>
                  </Flex>
                ))}
              </VStack>

          

            </Box>
          )}

        </Box>
      )}


      <Box mb={6}>
        <Heading fontSize={{ base: "lg", md: "xl" }} color="dark.900" mb={4}>
          Payout History
        </Heading>
        {payoutHistory.length > 0 ? (
          <VStack spacing={4} align="stretch">
            {payoutHistory.map((payout, index) => (
              <Box
                key={index}
                p={{ base: 3, md: 4 }}
                borderWidth="1px"
                borderRadius="md"
                bg="secondary.100"
                shadow="sm"
                _hover={{ shadow: "md" }}
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  justify="space-between"
                  align="center"
                  wrap="wrap"
                  gap={2}
                  color="primary.100"
                >
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    <strong>Date:</strong>{" "}
                    {new Date(payout.created * 1000).toLocaleDateString()}
                  </Text>
                  <Text fontSize={{ base: "sm", md: "md" }}>
                    <strong>Amount:</strong> ${(payout.amount / 100).toFixed(2)}
                  </Text>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={payout.status === "paid" ? "green.400" : "yellow.400"}
                  >
                    <strong>Status:</strong>{" "}
                    {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                  </Text>
                </Flex>
              </Box>
            ))}
          </VStack>
        ) : (
          <Text color="primary.500">No payouts found.</Text>
        )}
      </Box>
      <Divider borderColor="primary.500" my={6} />

    </Box>
  );
};



export default BillingInfo;